"use client";

import { useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { SuccessDialog } from "./SuccessDialog";
import emailJs from "@emailjs/browser";
import { Loader2 } from "lucide-react";

type Props = {
  title: string;
  subtitle: string;
  buttonLabel: string;
  footerContent: string;
};

const applicationFormSchema = z.object({
  firstName: z.string().min(1, "Enter your first name"),
  lastName: z.string().min(1, "Enter your last name"),
  email: z.email().min(1, "Enter your email"),
  phoneNumber: z.string().min(1, "Enter your Phone Number"),
  preferredProgram: z.string().min(1, "Enter your Preferred Programme"),
  highestEducationLevel: z
    .string()
    .min(1, "Enter your highest education level"),
  personalStatement: z.string().optional(),
});

export default function ApplicationForm({
  buttonLabel,
  subtitle,
  title,
  footerContent,
}: Props) {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const form = useForm<z.infer<typeof applicationFormSchema>>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      highestEducationLevel: "",
      personalStatement: "",
      phoneNumber: "",
      preferredProgram: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof applicationFormSchema>) => {
    const templateParams = {
      from_name: `${data.firstName} ${data.lastName}`,
      from_email: data.email,
      phone: data.phoneNumber,
      programme: data.preferredProgram,
      education: data.highestEducationLevel,
      personal_statement: data.personalStatement,
      time: new Date().toLocaleString(),
      to_name: "Zenith Academy Admissions",
      message_type: "Application Submission",
    };

    try {
      await emailJs.send(
        process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID || "your_service_id",
        process.env.NEXT_PUBLIC_EMAILJS_APPLICATION_TEMPLATE_ID ||
          "your_template_id",
        templateParams,
        process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY || "your_public_key"
      );

      setIsSuccessModalOpen(true);
      form.reset();
    } catch (error: any) {
      console.error("EmailJS error:", error);
      form.setError("firstName", {
        message: error.message,
      });
    }
  };

  return (
    <div
      id="apply-now-form"
      className="bg-[#FAFDFF] p-6 [box-shadow:0_6px_14px_0_rgba(1,27,43,0.08)] xl:w-[633px] w-full"
    >
      <h4 className="text-[#033D61] font-archivo mb-3 text-2xl font-semibold leading-9">
        {title}
      </h4>
      <p className="text-[#033D61] font-merriweather font-lg mb-[53px] font-normal leading-7">
        {subtitle}
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid md:grid-cols-2 grid-cols-1 gap-6"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                  First Name <span className="text-[#F50808]">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6"
                    placeholder="Enter your First Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                  Last Name <span className="text-[#F50808]">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6"
                    placeholder="Enter your Last Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                    Email Address <span className="text-[#F50808]">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6"
                      placeholder="Enter your Email Address"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                    Phone Number <span className="text-[#F50808]">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="tel"
                      className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6"
                      placeholder="Enter your Phone Number"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="preferredProgram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                    Preferred Programme{" "}
                    <span className="text-[#F50808]">*</span>
                  </FormLabel>
                  <FormControl>
                    <Select {...field} onValueChange={field.onChange}>
                      <SelectTrigger className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6">
                        <SelectValue placeholder="Select a Programme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FdA Health & Social Care (with Foundation Year)">
                          FdA Health & Social Care (with Foundation Year)
                        </SelectItem>
                        <SelectItem value="BA (Hons) International Hospitality Management (with Foundation Year)">
                          BA (Hons) International Hospitality Management (with
                          Foundation Year)
                        </SelectItem>
                        <SelectItem value="BSc (Hons) Sport Business Management (with Foundation Year)">
                          BSc (Hons) Sport Business Management (with Foundation
                          Year)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="highestEducationLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                    Highest Education Level{" "}
                    <span className="text-[#F50808]">*</span>
                  </FormLabel>
                  <FormControl>
                    <Select {...field} onValueChange={field.onChange}>
                      <SelectTrigger className="h-[60px] bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6">
                        <SelectValue placeholder="Select your education level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GCSE or equivalent">
                          GCSE or equivalent
                        </SelectItem>
                        <SelectItem value="A-Levels or equivalent">
                          A-Levels or equivalent
                        </SelectItem>
                        <SelectItem value="BTEC">BTEC</SelectItem>
                        <SelectItem value="Foundation course">
                          Foundation course
                        </SelectItem>
                        <SelectItem value="Undergraduate degree">
                          Undergraduate degree
                        </SelectItem>
                        <SelectItem value="Postgraduate degree">
                          Postgraduate degree
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="personalStatement"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#033D61] font-archivo text-base font-medium leading-6">
                    Personal Statement (Optional)
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      className="bg-[#FAFDFF] rounded-xl px-3 py-[18px] border-[#D3DEE5] focus:ring-0 focus:outline-none focus:border-[#D3DEE5] mt-4 w-full placeholder:text-[#033D61B3] text-[#033D61] text-sm font-normal leading-6 resize-none"
                      placeholder="Tell us why you're interested in this programme and what you hope to achieve"
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription className="text-[#033D61] font-archivo text-base font-medium leading-6 mt-6">
                    Share your motivation, relevant experience, and career goals
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2 w-full flex flex-col items-center justify-center gap-4">
            <Button
              type="submit"
              className="bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61] cursor-pointer h-12 rounded-[40px] w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="size-8! shrink-0 p-1 rounded-full text-white! transition-all duration-300 ease-in-out animate-spin" />
              ) : (
                buttonLabel
              )}
            </Button>
            <div className="text-[#033D61B3] max-w-[555px] text-center font-merriweather text-sm font-normal leading-6">
              {footerContent}
            </div>
          </div>
        </form>
      </Form>
      <SuccessDialog
        open={isSuccessModalOpen}
        setOpen={setIsSuccessModalOpen}
      />
    </div>
  );
}
